/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/CompanySelector/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger } from "../../utils/logger.js";
import React, { useState, useMemo } from "react";
import Select, { components } from "react-select";
import { useTranslation } from "react-i18next";
import { List } from "react-window";

// Virtualized MenuList for react-select
const MenuList = (props) => {
  const { children, maxHeight } = props;
  const items = React.Children.toArray(children);
  const height = Math.min(maxHeight, items.length * 45);

  return (
    <components.MenuList {...props}>
      <List
        rowHeight={45}
        rowCount={items.length}
        height={height}
        width="100%"
        rowProps={{}}
        rowComponent={({ index, style }) => (
          <div style={style}>{items[index]}</div>
        )}
      />
    </components.MenuList>
  );
};

const Option = (props) => {
  const { isSelected, label, data, selectProps } = props;

  const isSelectAll = data._id === "__all__";

  let checked = isSelected;
  let indeterminate = false;

  if (isSelectAll && selectProps.isMulti) {
    const selected = selectProps.value || [];
    const filtered = selectProps.filteredCompanies || [];

    if (selected.length === 0) {
      checked = false;
    } else if (selected.length === filtered.length) {
      checked = true;
    } else {
      indeterminate = true;
    }
  }

  return (
    <components.Option {...props}>
      {selectProps.isMulti && (
        <input
          type="checkbox"
          checked={checked}
          ref={(el) => {
            if (el) {
              el.indeterminate = indeterminate;
            }
          }}
          onChange={() => null}
          style={{ marginRight: 8 }}
        />
      )}
      {label}
    </components.Option>
  );
};

const MultiValue = (props) => {
  const { t } = useTranslation();
  const { index, getValue } = props;
  const maxToShow = 1;

  const selected = getValue();

  if (index < maxToShow) {
    return <components.MultiValue {...props} />;
  }

  if (index === maxToShow) {
    return (
      <div style={{ marginLeft: 6 }}>
        +{selected.length - maxToShow} {t("pages.reports.more")}
      </div>
    );
  }

  return null;
};

const CompanySelector = ({
  companies = [],
  loading,
  isDisabled,
  onChange,
  placeholder,
  styles,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");

  const filteredCompanies = useMemo(() => {
    if (!inputValue) {
      return companies;
    }
    const lower = inputValue.toLowerCase();
    return companies.filter((c) => c.search.startsWith(lower));
  }, [companies, inputValue]);

  return (
    <Select
      isLoading={loading}
      isDisabled={isDisabled}
      options={filteredCompanies}
      filterOption={null}
      components={{ MenuList }}
      placeholder={placeholder}
      onChange={onChange}
      onInputChange={(value) => setInputValue(value)}
      maxMenuHeight={200} // virtualized height
      getOptionLabel={(option) => option.nombreComercial}
      getOptionValue={(option) => option._id}
      noOptionsMessage={() => t("pages.reports.noCompaniesFound")}
      styles={styles}
    />
  );
};

const MultiCompanySelector = ({
  companies = [],
  loading,
  isDisabled,
  selectedCompanies = [],
  onChange,
  placeholder,
  styles,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");

  const SELECT_ALL_OPTION = {
    _id: "__all__",
    nombreComercial: t("pages.reports.selectAllCompanies"),
  };

  const filteredCompanies = useMemo(() => {
    if (!inputValue) {
      return companies;
    }
    const lower = inputValue.toLowerCase();
    return companies.filter((c) => c.search.startsWith(lower));
  }, [companies, inputValue]);

  const computedOptions = useMemo(() => {
    return [SELECT_ALL_OPTION, ...filteredCompanies];
  }, [filteredCompanies]);

  const handleChange = (selected) => {
    logger.info("handleChange: ", { data: selected });

    if (!selected) {
      onChange([]);
      return;
    }

    const selectedArray = Array.isArray(selected) ? selected : [selected];

    const isSelectAllClicked = selectedArray.some(
      (opt) => opt._id === "__all__",
    );

    if (isSelectAllClicked) {
      const currentSelection = selectedArray.filter(
        (opt) => opt._id !== "__all__",
      );

      const isAllSelected =
        currentSelection.length === filteredCompanies.length;

      if (isAllSelected) {
        // unselect all filtered
        const remaining = (selectedCompanies || []).filter(
          (v) => !filteredCompanies.some((f) => f._id === v._id),
        );
        onChange(remaining);
      } else {
        // select all filtered (merge with existing)
        const merged = [
          ...(selectedCompanies || []),
          ...filteredCompanies.filter(
            (f) => !(selectedCompanies || []).some((v) => v._id === f._id),
          ),
        ];
        onChange(merged);
      }
    } else {
      onChange(selectedArray);
    }
  };

  return (
    <Select
      value={selectedCompanies}
      isLoading={loading}
      isDisabled={isDisabled}
      isMulti={true}
      hideSelectedOptions={false}
      closeMenuOnSelect={false}
      options={computedOptions}
      filterOption={null}
      components={{ MenuList, Option, MultiValue }}
      placeholder={placeholder}
      onChange={handleChange}
      onInputChange={(value) => setInputValue(value)}
      maxMenuHeight={200} // virtualized height
      getOptionLabel={(option) => option.nombreComercial}
      getOptionValue={(option) => option._id}
      noOptionsMessage={() => t("pages.reports.noCompaniesFound")}
      styles={styles}
    />
  );
};

export { CompanySelector, MultiCompanySelector };
